import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ScanSearch, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";

// Global PIN jump (Phase 65): paste / type / hardware-scan a property PIN anywhere
// and go straight to that property. Works in every staff portal.
export function PinSearch({ code, compact = false }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);   // compact (mobile) dialog
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();

  // "/" focuses the inline search (desktop) when not already typing somewhere
  useEffect(() => {
    if (compact) return;
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)
          && !document.activeElement?.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compact]);

  const jump = async (e) => {
    e?.preventDefault();
    const pin = value.trim();
    if (!pin || busy) return;
    setBusy(true);
    try {
      const r = await api.get(`/t/${code}/properties/pin-lookup`, { params: { pin } });
      const p = r.data;
      toast.success(`${p.pin} — ${p.owner_name || "Property found"}`);
      setValue("");
      setOpen(false);
      // route by the portal the user is currently in (role as fallback)
      const role = auth?.user?.role;
      if (role === "tax_collector" || location.pathname.includes("/collector")) {
        navigate(`/${code}/collector/property/${p.id}`);
      } else if (location.pathname.includes("/supervisor") || role === "supervisor") {
        navigate(`/${code}/supervisor/properties?focus=${p.id}`);
      } else {
        navigate(`/${code}/admin/properties?focus=${p.id}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (autoFocus = false) => (
    <form onSubmit={jump} className="relative flex items-center gap-1.5 w-full">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input ref={autoFocus ? undefined : inputRef} autoFocus={autoFocus}
          value={value} onChange={(e) => setValue(e.target.value)}
          placeholder={compact ? "Scan or type PIN..." : "Jump to PIN...  ( / )"}
          className={compact ? "pl-8 h-10" : "pl-8 h-8 w-44 focus:w-56 transition-[width] duration-200 text-sm"}
          autoComplete="off" autoCapitalize="characters" spellCheck={false}
          data-testid={compact ? "pin-search-input-mobile" : "pin-search-input"} />
      </div>
      {compact && (
        <Button type="submit" size="sm" className="h-10" disabled={busy || !value.trim()} data-testid="pin-search-go-button">
          {busy ? "..." : "Go"}
        </Button>
      )}
    </form>
  );

  if (!compact) return field();

  return (
    <>
      <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/15 h-9 w-9"
        onClick={() => setOpen(true)} title="Jump to PIN" data-testid="pin-search-open-button">
        <ScanSearch className="h-5 w-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm top-[30%]" data-testid="pin-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <ScanSearch className="h-4 w-4" /> Jump to property PIN
            </DialogTitle>
          </DialogHeader>
          {field(true)}
          <p className="text-[11px] text-muted-foreground">
            Paste, type, or use a barcode scanner — press Go / Enter to open the property.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
